'use client';

import { Skeleton } from '@mui/material';
import { useRouter } from 'next/navigation';

import { appRoutes } from '@/appRoutes';
import {
  ChevronIcon,
  EmptyState,
  MarkdownRenderer,
  SecondaryButton,
} from '@web/ui';

import styles from './PublicLegalDocumentPage.module.scss';

type PublicLegalDocumentPageProps = {
  title: string;
  description?: string;
  markdownContent?: string | null;
  isLoading?: boolean;
  isError?: boolean;
};

export function PublicLegalDocumentPage({
  title,
  description,
  markdownContent,
  isLoading = false,
  isError = false,
}: PublicLegalDocumentPageProps) {
  const router = useRouter();
  const resolvedMarkdown = markdownContent?.trim() ?? '';

  const handleBack = () => {
    router.push(appRoutes.home);
  };

  const handleScrollUp = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollDown = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <main className={styles.publicLegalPage}>
      <header className={styles.publicLegalPage__header}>
        <div className={styles.publicLegalPage__backControl}>
          <SecondaryButton
            ariaLabel="Back"
            onClick={handleBack}
            variant="medium"
          >
            <ChevronIcon direction="left" size={14} />
          </SecondaryButton>
        </div>
        <h1 className={styles.publicLegalPage__title}>{title}</h1>
        {description ? (
          <p className={styles.publicLegalPage__subtitle}>{description}</p>
        ) : null}
      </header>

      <section
        className={`card-container-glass ${styles.publicLegalPage__panel}`}
      >
        {isLoading ? (
          <div
            className={styles.publicLegalPage__skeleton}
            aria-label={`${title} loading`}
          >
            <Skeleton variant="text" width="42%" height={34} />
            <Skeleton variant="text" width="96%" height={22} />
            <Skeleton variant="text" width="88%" height={22} />
            <Skeleton variant="rounded" width="100%" height={180} />
            <Skeleton variant="text" width="92%" height={22} />
            <Skeleton variant="text" width="78%" height={22} />
          </div>
        ) : isError ? (
          <p className={styles.publicLegalPage__state}>
            Unable to load this document right now.
          </p>
        ) : resolvedMarkdown ? (
          <MarkdownRenderer markdownContent={resolvedMarkdown} showLogo />
        ) : (
          <EmptyState title="No active document is available right now." />
        )}
      </section>

      <div className={styles.publicLegalPage__scrollControls}>
        <SecondaryButton
          ariaLabel="Scroll up"
          onClick={handleScrollUp}
          variant="mini"
        >
          <ChevronIcon direction="up" size={14} />
        </SecondaryButton>
        <SecondaryButton
          ariaLabel="Scroll down"
          onClick={handleScrollDown}
          variant="mini"
        >
          <ChevronIcon direction="down" size={14} />
        </SecondaryButton>
      </div>
    </main>
  );
}
